import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, FileText, Loader2, Play, TrendingUp,
  Users, Mail, DollarSign, MessageSquare, Zap, Package as PackageIcon,
} from "lucide-react";
import { FloatingOrbs, PulseDot } from "@/components/landing/AmbientMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sb_admin_token") || ""}` });

const dateRange = (start, end) => `${start} → ${end}`;

/**
 * /admin/analytics/reports — history of weekly reports (native replacement for
 * Monday agent's weekly summary comment). Also supports /admin/analytics/reports/:id
 * for a single report deep-view (matches the push notification link).
 */
const AdminWeeklyReports = () => {
  const { reportId } = useParams();
  const [reports, setReports] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports/weekly?limit=30`, { headers: authHeaders() });
      setReports(data.reports || []);
      if (reportId) {
        const match = (data.reports || []).find((r) => r.id === reportId);
        if (match) {
          setCurrent(match);
        } else {
          // Fetch by id directly
          const { data: one } = await axios.get(`${API}/admin/reports/weekly/${reportId}`, { headers: authHeaders() });
          setCurrent(one);
        }
      } else if ((data.reports || []).length > 0) {
        setCurrent(data.reports[0]);
      }
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reportId]);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/reports/weekly/run`, {}, { headers: authHeaders() });
      toast.success("Weekly report generated.");
      setCurrent(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report run failed");
    } finally {
      setRunning(false);
    }
  };

  const n = current?.numbers || {};

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden" data-testid="admin-weekly-reports">
      <FloatingOrbs colors={["#00f0ff", "#22c55e", "#a78bfa"]} />

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-8">
          <div>
            <Link to="/admin/analytics" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition mb-3">
              <ArrowLeft size={12} /> Back to analytics
            </Link>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-3">
              <PulseDot color="#00f0ff" size={7} />
              Weekly Reports
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Every Monday, <span className="text-[#00f0ff] text-glow-cyan">the week</span> in one glance.
            </h1>
            <p className="text-[#a395c3] text-sm mt-2 max-w-xl">
              Native replacement for the Monday.com Submission Intake Agent. Auto-runs every Monday 8 AM ET, emails you, saves here, and pings your phone.
            </p>
          </div>
          <button
            onClick={runNow}
            disabled={running}
            data-testid="run-weekly-now"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition disabled:opacity-50"
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            Run one now
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar: report history */}
          <aside className="rounded-2xl border border-white/10 bg-white/[0.025] p-3 h-fit md:sticky md:top-6" data-testid="reports-sidebar">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-3 px-2">History</div>
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 size={18} className="animate-spin text-[#00f0ff]" /></div>
            ) : reports.length === 0 ? (
              <div className="text-xs text-[#a395c3] italic px-2 py-4">No reports yet — hit &ldquo;Run one now&rdquo;.</div>
            ) : (
              <ul className="space-y-1">
                {reports.map((r) => {
                  const isActive = current?.id === r.id;
                  return (
                    <li key={r.id}>
                      <button
                        onClick={() => setCurrent(r)}
                        data-testid={`report-item-${r.id}`}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition ${
                          isActive
                            ? "bg-[#00f0ff]/12 border border-[#00f0ff]/40 text-[#00f0ff]"
                            : "hover:bg-white/[0.04] text-white/75 border border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] mb-0.5 opacity-70">
                          <FileText size={9} /> {new Date(r.created_at).toLocaleDateString()}
                        </div>
                        <div className="font-semibold text-[11px] leading-tight">
                          {r.period_start} → {r.period_end}
                        </div>
                        <div className="text-[10px] opacity-60 mt-0.5">
                          {r.numbers?.booked || 0} booked · ${(r.numbers?.revenue_projected || 0).toLocaleString()}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Main: current report */}
          {current ? (
            <div className="space-y-5" data-testid="report-view">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-1">Weekly report</div>
                <div className="text-xl font-display font-bold mb-1">{dateRange(current.period_start, current.period_end)}</div>
                <div className="text-[11px] text-white/45">
                  Generated {new Date(current.created_at).toLocaleString()}
                  {current.delivered_to?.length > 0 && ` · Emailed to ${current.delivered_to.length} admin(s)`}
                </div>
              </div>

              {/* Stat grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={DollarSign} label="Revenue projected" value={`$${(n.revenue_projected || 0).toLocaleString()}`} color="#22c55e" />
                <StatCard icon={TrendingUp} label="Booked" value={n.booked || 0} color="#00f0ff" />
                <StatCard icon={Zap} label="Leads captured" value={n.chat_leads_captured || 0} color="#a78bfa" />
                <StatCard icon={Users} label="Guest requests" value={n.guest_requests || 0} color="#ff5577" />
                <StatCard icon={MessageSquare} label="Chats" value={n.chat_sessions || 0} color="#00f0ff" />
                <StatCard icon={TrendingUp} label="Capture rate" value={`${n.contact_capture_rate || 0}%`} color="#22c55e" />
                <StatCard icon={PackageIcon} label="Total bookings" value={n.total_bookings || 0} color="#a78bfa" />
                <StatCard icon={Mail} label="Escalations" value={n.escalated_chats || 0} color="#ff5577" />
              </div>

              {/* Top packages */}
              {current.top_packages?.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-3">Top packages this week</div>
                  <ul className="space-y-2">
                    {current.top_packages.map((p, i) => (
                      <li key={p.package || i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-white/40">#{i + 1}</span>
                          <span className="text-sm font-semibold">{p.label}</span>
                        </div>
                        <div className="text-xs text-[#a395c3]">
                          <span className="font-mono">{p.count}×</span>
                          <span className="mx-2">·</span>
                          <span className="text-[#22c55e] font-semibold">${p.revenue.toLocaleString()}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Escalations */}
              {current.escalations?.length > 0 && (
                <div className="rounded-2xl border border-[#ff0055]/25 bg-[#ff0055]/[0.04] p-5">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#ff5577] mb-3">Notable escalations</div>
                  <ul className="space-y-2">
                    {current.escalations.map((e) => (
                      <li key={e.session_id} className="rounded-lg bg-white/[0.03] border border-white/5 p-3 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{e.name}</div>
                          {e.reason && <div className="text-[11px] text-[#a395c3] mt-0.5 line-clamp-2">{e.reason}</div>}
                        </div>
                        <Link to="/admin/chat-leads" className="text-[10px] text-[#00f0ff] hover:text-white whitespace-nowrap">
                          Open →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Narrative */}
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#00f0ff]/[0.06] to-transparent p-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff] mb-3">Narrative</div>
                <div className="whitespace-pre-line text-[14px] leading-relaxed text-white/85">
                  {current.narrative}
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-24"><Loader2 size={22} className="animate-spin text-[#00f0ff]" /></div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.015] p-10 text-center">
              <FileText size={28} className="mx-auto text-white/30 mb-3" />
              <div className="font-display font-bold text-lg mb-1">No reports yet</div>
              <p className="text-sm text-[#a395c3] mb-4">Reports auto-generate every Monday at 8 AM ET.</p>
              <button
                onClick={runNow}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-xs hover:bg-white transition"
              >
                <ArrowRight size={11} /> Generate one now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div
    className="rounded-xl border p-3"
    style={{ background: `${color}0d`, borderColor: `${color}38` }}
    data-testid={`report-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center gap-2 mb-1.5">
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center border"
        style={{ background: `${color}20`, borderColor: `${color}55`, color }}
      >
        <Icon size={11} />
      </div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">{label}</div>
    </div>
    <div className="text-2xl font-display font-black" style={{ color }}>
      {value}
    </div>
  </div>
);

export default AdminWeeklyReports;
